import {
	type BulkAclEntity,
	type InvestmentReferenceListEntry,
	InvestmentExportConverterType,
} from "@eu.mdotm.api/sphere";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { aclByArea, roleByArea } from "$root/functional-areas/acl/checkers/all";
import { validateACLPermissions } from "$root/functional-areas/acl/checkers/shared";
import { useUpdateAccessListControlDialog } from "$root/functional-areas/acl/hook/useUpdateAccessListControlDialog";
import {
	checkBulkActionAvailability,
	BulkActionReviewSections,
	spawnBulkActionReviewDialog,
} from "$root/functional-areas/portfolio-studio/bulk-actions/common";
import { ReferenceActionUnavailableReason } from "$root/functional-areas/portfolio-studio/bulk-actions/references-actions";
import { referenceListActionValidators } from "$root/functional-areas/portfolio-studio/bulk-actions/references-actions/validators";
import { referenceReasonToMessageMap } from "$root/functional-areas/portfolio-studio/bulk-actions/references-actions/reason-messages";
import { spawnBulkExportAction } from "$root/functional-areas/portfolio-studio/bulk-actions/spawn-bulk-export-action";
import { spawnBulkShareAction } from "$root/functional-areas/portfolio-studio/bulk-actions/spawn-bulk-share-action";
import { benchmarkEntityManagementActions } from "$root/functional-areas/benchmark/entity-management";
import { portfolioEntityManagementActions } from "$root/functional-areas/portfolio/entity-management";
import { useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules, useAgGridSelectedNodes } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { parallelize, preventSubmitOnPressEnter } from "$root/utils";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { TextInput, BulkActions, type BulkActionItem } from "@mdotm/shadcn/mdotm/components";
import { useDebouncedMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { noop, switchExpr, unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { GetContextMenuItems } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { type FC, useCallback, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { type ReferenceItem, createReferenceRows, multipleEntityDelete, notifyUser } from "../common";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { groupBy } from "@mdotm/utils/collections";
import { runWithErrorReporting } from "$root/api/error-reporting";

type ReferenceListProps = {
	references: InvestmentReferenceListEntry[];
	benchmarks: InvestmentReferenceListEntry[];
};

const ReferenceList: FC<ReferenceListProps> = ({ benchmarks, references }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [gridEl, setGridEl] = useState<AgGridReact<ReferenceItem> | null>(null);
	const selectedNodes = useAgGridSelectedNodes(gridEl);

	const user = useUserValue();
	const { t } = useTranslation();

	const { aclSpawn: targetPortfolioSpawn } = useUpdateAccessListControlDialog("REFERENCE");

	const { value: debouncedLowerCaseSearchQuery } = useDebouncedMemo(
		() => searchQuery.toLocaleLowerCase(),
		[searchQuery],
		{
			debounceInterval: 200,
		},
	);

	const rows = useMemo(() => createReferenceRows(benchmarks, references), [references, benchmarks]);
	const filteredRows = useMemo(
		() =>
			rows.filter((item) =>
				[item.name ?? "", item.status ?? ""].some((el) => el.toLowerCase().includes(debouncedLowerCaseSearchQuery)),
			),
		[debouncedLowerCaseSearchQuery, rows],
	);

	const getContextMenuItems = useCallback<GetContextMenuItems<ReferenceItem, any>>(
		(row) => {
			const reference = row.node?.data;
			if (!reference) {
				return ["copy"];
			}

			const area = reference.type === "Benchmark" ? "benchmark" : ("targetPortfolio" satisfies keyof typeof aclByArea);
			const entityManagementActions =
				reference.type === "LegacyBenchmark"
					? benchmarkEntityManagementActions(user, { ...reference, uuid: reference.uuid })
					: portfolioEntityManagementActions(
							user,
							{ ...reference, uuid: reference.uuid },
							undefined,
							"targetPortfolio",
						);

			if (reference.type === "LegacyBenchmark") {
				return aclByArea[area].canDelete(user.id, reference.richAcl?.acl ?? [])
					? [
							{
								icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
								disabled: !entityManagementActions.deleteAsync,
								action: unpromisify(entityManagementActions.deleteAsync ?? noop),
								"data-qualifier": "PortfolioStudio/ReferenceList/DropdownMenu/DropdownItem(Delete)",
								name: "Delete",
							},
						]
					: ["copy"];
			}

			return [
				...(aclByArea[area].canDelete(user.id, reference.richAcl?.acl ?? [])
					? [
							{
								icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
								disabled: !entityManagementActions.deleteAsync,
								action: unpromisify(entityManagementActions.deleteAsync ?? noop),
								"data-qualifier": "PortfolioStudio/ReferenceList/DropdownMenu/DropdownItem(Delete)",
								name: "Delete",
							},
						]
					: []),
				{
					icon: renderToStaticMarkup(<Icon icon="Content-Copy" size={16} />),
					disabled: !entityManagementActions.duplicateAsync,
					action: unpromisify(entityManagementActions.duplicateAsync ?? noop),
					"data-qualifier": "PortfolioStudio/ReferenceList/DropdownMenu/DropdownItem(Duplicate)",
					name: "Duplicate",
				},
				...(aclByArea[area].canRename(user.id, reference.richAcl?.acl ?? [])
					? [
							{
								icon: renderToStaticMarkup(<Icon icon="Edit" size={16} />),
								disabled: !entityManagementActions.renameAsync,
								action: unpromisify(entityManagementActions.renameAsync ?? noop),
								"data-qualifier": "PortfolioStudio/ReferenceList/DropdownMenu/DropdownItem(Rename)",
								name: "Rename",
							},
						]
					: []),
				...(switchExpr(reference.type, {
					References: () =>
						validateACLPermissions(user.id, reference.richAcl?.acl ?? [], roleByArea.targetPortfolio.EDITOR),
					Benchmark: () => validateACLPermissions(user.id, reference.richAcl?.acl ?? [], roleByArea.benchmark.EDITOR),
					LegacyBenchmark: () =>
						validateACLPermissions(user.id, reference.richAcl?.acl ?? [], roleByArea.benchmark.EDITOR),
				})
					? [
							{
								icon: renderToStaticMarkup(<Icon icon="share" size={16} />),
								action: unpromisify(async () => {
									switch (reference.type) {
										case "Benchmark":
										case "References": {
											await targetPortfolioSpawn(reference.name, reference.uuid);
											break;
										}
										case "LegacyBenchmark": {
											throw new Error("impl removed");
										}
									}
								}),
								"data-qualifier": "PortfolioStudio/ReferenceList/DropdownMenu/DropdownItem(Share)",
								name: "Share",
							},
						]
					: []),
			];
		},
		[targetPortfolioSpawn, user],
	);

	const columns = columnsDefinition.useReferenceList();
	return (
		<>
			<div className="min-h-0 min-w-0 flex flex-1 basis-0! flex-col rounded-lg bg-white px-4 py-6">
				<TextInput
					value={searchQuery}
					name="search"
					maxLength={60}
					onChangeText={setSearchQuery}
					placeholder={t("PORTFOLIOS.CONSTRAINTS_TARGETS.PORTFOLIO_LIST")}
					onKeyDown={preventSubmitOnPressEnter}
					style={{ width: 692 }}
					leftContent={<Icon icon="Search" size="1.4em" />}
					className="mb-4"
				/>
				<AgGridWithModules<ReferenceItem>
					{...agExtra.checkboxColumn({
						mode: "multiRow",
						selectAll: "currentPage",
					})}
					{...agExtra.usePersistColState(persistedColStateKeys["tableV3-referenceList"])}
					innerRef={setGridEl}
					getContextMenuItems={getContextMenuItems}
					columnDefs={columns}
					rowData={filteredRows}
				/>
			</div>
			<BulkActions
				variant="floating"
				count={selectedNodes.length}
				onClear={() => gridEl?.api.deselectAll()}
				actions={[
					{
						label: "Delete",
						icon: "Delete",
						onClick() {
							const reasonKeys = Object.values(ReferenceActionUnavailableReason);
							const availabilityResult = checkBulkActionAvailability(
								reasonKeys,
								referenceListActionValidators.delete,
								selectedNodes,
							);
							const [available, unavailable] = availabilityResult;

							async function onContinue() {
								const response = await multipleEntityDelete({ area: "References", items: available });
								gridEl?.api.deselectAll();
								notifyUser({ t, succeded: response.succeded, area: "References" });
							}

							if (unavailable === null) {
								spawnYesNoDialog({
									size: "large",
									header: t("PORTFOLIOS.MODAL.BULK_DELETE.TITLE"),
									yesButton: t("BUTTON.DELETE"),
									noButton: t("BUTTON.CANCEL"),
									children: t("PORTFOLIOS.MODAL.BULK_DELETE.DESCRIPTION", {
										size: available.length,
									}),
									onSubmitAsync: onContinue,
								})
									.then(noop)
									.catch(noop);
							} else {
								spawnBulkActionReviewDialog({
									initialState: null,
									header: "Delete: review and confirm",
									children: (
										<BulkActionReviewSections
											availabilityResult={availabilityResult}
											reasonToMessage={referenceReasonToMessageMap}
											getRedirectUrl={() => ""}
											hideRedirectUrl
											showEntityNames
											labels={{ entitySingular: "Reference", entityPlural: "References" }}
										/>
									),
									canConfirm: available.length > 0,
									onContinue,
								}).catch(noop);
							}
						},
						"data-qualifier": "PortfolioStudio/ReferenceList/BatchAction(Delete)",
					},
					{
						label: "Share",
						icon: "share",
						async onClickAsync() {
							runWithErrorReporting(
								async () => {
									await spawnBulkShareAction({
										reasonKeys: Object.values(ReferenceActionUnavailableReason),
										validator: referenceListActionValidators.share,
										entities: selectedNodes,
										reasonToMessage: referenceReasonToMessageMap,
										labels: { entitySingular: "Reference", entityPlural: "References" },
										getRedirectUrl: () => "",
										hideRedirectUrl: true,
										showEntityNames: true,
										area: "targetPortfolio",
										customerAndLinkedAclProvider: (available) => () =>
											parallelize(
												available.map(
													(reference) => () =>
														reference.type === "LegacyBenchmark"
															? referenceRepository.getBenchmarkACL.fetchQuery(reference.uuid!)
															: referenceRepository.getReferenceACL.fetchQuery(reference.uuid!),
												),
											),
										getEntityMetadata: (reference) => ({
											uuid: reference.uuid!,
											name: reference.name!,
											entityType: reference.type === "LegacyBenchmark" ? "BENCHMARK" : "REFERENCE",
										}),
										async saveAclHandler(available, { aclByEntity }) {
											const referencesByType = groupBy(available, (reference) => reference.type);
											if (referencesByType.LegacyBenchmark && referencesByType.LegacyBenchmark?.length > 0) {
												const legacyBenchmarkToShare = referencesByType.LegacyBenchmark.map(
													(reference): BulkAclEntity => ({
														accessControlList: aclByEntity[reference.uuid!],
														entityUuid: reference.uuid!,
													}),
												);
												await referenceRepository.bulkBenchmarkShare(legacyBenchmarkToShare);
											}
											const references = [
												...(referencesByType.References ?? []),
												...(referencesByType.Benchmark ?? []),
											];
											if (references?.length > 0) {
												const referencesToShare = references.map(
													(reference): BulkAclEntity => ({
														accessControlList: aclByEntity[reference.uuid!],
														entityUuid: reference.uuid!,
													}),
												);
												await referenceRepository.bulkReferenceShare(referencesToShare);
											}
										},
									});
								},
								{ area: "alerts", attemptedOperation: { message: "Bulk action entities error" } },
							);
						},
						"data-qualifier": "PortfolioStudio/ReferenceList/BatchAction(Share)",
					},
					...(hasAccess(user, { requiredService: "EXPORT" }) &&
					(user.exportFormats ?? []).includes(InvestmentExportConverterType.SphereBulkTemplateConverter)
						? [
								{
									label: "Download",
									icon: "Dowload",
									dropdownItems: [
										{
											label: `${t(`EXPORT.${InvestmentExportConverterType.SphereBulkTemplateConverter}`)} template`,
											icon: "xls",
											onClickAsync: () =>
												spawnBulkExportAction({
													reasonKeys: Object.values(ReferenceActionUnavailableReason),
													validator: referenceListActionValidators.export,
													entities: selectedNodes,
													reasonToMessage: referenceReasonToMessageMap,
													labels: { entitySingular: "Reference", entityPlural: "References" },
													getRedirectUrl: () => "",
													hideRedirectUrl: true,
													showEntityNames: true,
													area: "target-portfolio",
													downloadBulkTemplate: referenceRepository.downloadBulkTemplate,
													onStarted: () => gridEl?.api.deselectAll(),
												}),
											"data-qualifier": "PortfolioStudio/ReferenceList/BatchAction(Download)/BulkSphereTemplate",
										},
									],
									"data-qualifier": "PortfolioStudio/ReferenceList/BatchAction(Download)",
								} satisfies BulkActionItem,
							]
						: []),
				]}
			/>
		</>
	);
};

export default ReferenceList;
